<script setup>
import { computed, toRefs } from 'vue';
import BaseBadge from '@/components/Base/BaseBadge.vue';
import BaseButton from '@/components/Base/BaseButton.vue';
import BaseIcon from '@/components/Base/BaseIcon.vue';
import ApplicantCard from '@/components/Elements/Applicant/ApplicantCard.vue';
import BaseTooltip from '@/components/Base/BaseTooltip.vue';
import englishAssessmentUtils from '@/utils/englishAssessmentUtils';
import { STATUS_MAP } from '@/constants/assessments';

/**
 * @typedef {Object} Achievement
 * @property {string} test_link
 * @property {string} report_url
 * @property {'sent'|'completed'|'failed'|'expired'|'processing'} status
 * @property {string|null} score
 * @property {Object<string, string>|null} detailed_scores
 * @property {boolean} is_verified
 * @property {string|null} started_at
 * @property {string|null} finished_at
 * @property {string|null} expires_at
 */

const props = defineProps({
  achievement: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});
const { achievement } = toRefs(props);

const score = computed(() => achievement.value?.score || '');

const passingThreshold = computed(
  () => achievement.value?.cefr_level_threshold || ''
);

const status = computed(() =>
  achievement.value?.status
    ? STATUS_MAP[achievement.value?.status]
    : {
        label: 'Unknown',
        color: 'gray',
        iconColor: null,
        tooltip: null,
      }
);

const detailedScoreItems = computed(() =>
  englishAssessmentUtils.getOrderedScoreDetails(
    achievement.value?.detailed_scores
  )
);

function openReport() {
  englishAssessmentUtils.openReport(achievement.value?.report_url);
}
</script>

<template>
  <ApplicantCard
    title="English Assessment"
    custom-class="p-4"
  >
    <template #title>
      <div class="flex justify-between items-center">
        <div class="text-body-md text-proxify-gray-900 font-bold">
          English Assessment
        </div>
        <BaseBadge
          v-if="status.color"
          size="sm"
          :color="status.color"
        >
          {{ status.label }}
          <template #append>
            <BaseIcon
              v-if="status.iconColor"
              name="info-circle"
              :color="status.iconColor"
            />
            <BaseTooltip
              v-if="status.tooltip"
              max-width="216px"
              :offset="[0, 10]"
            >
              <div v-if="achievement.status === 'failed'">
                There was an issue running this test. Please contact the Talent
                Team for support.
              </div>
              <div
                v-if="achievement.status === 'completed'"
                class="flex flex-col gap-y-2"
              >
                <div class="text-body-sm font-semibold">English Assessment</div>
                <div class="flex gap-x-2 text-body-xs text-proxify-gray-700">
                  Passing level:
                  <span class="font-semibold text-proxify-gray-900">
                    {{ passingThreshold }}
                  </span>
                </div>
                <div class="flex gap-x-2 text-body-xs text-proxify-gray-700">
                  Level:
                  <span class="font-semibold text-proxify-gray-900">
                    {{ score }}
                  </span>
                </div>
                <div
                  v-for="(item, index) in detailedScoreItems"
                  :key="index"
                  class="flex gap-x-2 text-body-xs font-normal text-proxify-gray-600"
                >
                  <span>{{ item.label }}:</span>
                  <span>{{ item.value }}</span>
                </div>
                <div class="text-body-xs text-proxify-gray-400">
                  SmallTalk2Me Results
                </div>
              </div>
            </BaseTooltip>
          </template>
        </BaseBadge>
      </div>
    </template>
    <div class="flex flex-col gap-y-2">
      <BaseButton
        color="transparent"
        rounded
        text-color="proxify-gray-900"
        class="p-0 text-body-sm font-semibold"
        align="left"
        padding="none"
        :disable="!achievement.status || achievement.status !== 'completed'"
        @click="openReport"
      >
        Open report
        <BaseIcon
          name="link-external-01"
          class="ml-1 w-5 h-5"
        />
      </BaseButton>
    </div>
  </ApplicantCard>
</template>

<style lang="scss" scoped>
.q-btn.disabled {
  @apply text-proxify-gray-300 opacity-100 !important;
}
</style>
